import { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Typography, useTheme } from "@mui/material";
import {
  CheckCircleOutline as DoneIcon,
  ErrorOutline as FailedIcon,
  HistoryToggleOff as ExpiredIcon,
  Undo as UndoIcon,
  WarningAmberRounded as WarnIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import AgentAttachment from "./AgentAttachment";
import { agentChatStyles as styles } from "./styles";

/** The pricelist editor's own warnings (active orders): shown like its dialogs, before Confirm. */
const WARNINGS = ["activeOrdersVariants", "activeOrdersPrices", "activeOrdersVisitPrice"];

// A write the assistant proposed or did (COMY1-391), under its reply:
//   proposed  the Confirm card — every detail in plain words, naming the member
//             or community and who is told; Confirm / Edit / Cancel
//   executed  the receipt; Undo while the server allows it (a minute)
//   declined, expired, failed, undone — what became of it
// Every decision goes to the server by the action's id; this card only shows
// what the server answered.

const msUntil = (iso) => (iso ? new Date(iso).getTime() - Date.now() : -1);

export default function AgentActionCard({ action, onDecide, onExpire, onEdit, conversationId }) {
  const { t } = useTranslation("agent");
  const theme = useTheme();
  const { status, card, busy } = action;

  // a pending card's 10 minutes, and the Undo minute, run out on screen too
  const [, tick] = useState(0);
  useEffect(() => {
    const until = status === "proposed" ? action.expiresAt : status === "executed" ? action.undoUntil : null;
    const ms = msUntil(until);
    if (!until) return undefined;
    if (ms <= 0) {
      if (status === "proposed") onExpire(action);
      return undefined;
    }
    const timer = setTimeout(() => {
      if (status === "proposed") onExpire(action);
      else tick((n) => n + 1);
    }, ms + 250);
    return () => clearTimeout(timer);
  }, [status, action, onExpire]);

  const canUndo = status === "executed" && msUntil(action.undoUntil) > 0;
  const note =
    action.message ??
    (status === "declined" ? t("actions.declined") : status === "expired" ? t("actions.expired") : null);

  return (
    // values sit in the panel's direction; each is isolated (<bdi>) so a Latin name keeps its own order
    <Box sx={styles.card(status)} role="group" aria-label={card?.title ?? action.receipt ?? t("actions.write")}>
      {card && <Typography sx={styles.cardTitle}>{card.title}</Typography>}
      {card && status === "proposed" && (
        <Box component="dl" sx={styles.cardFields}>
          {(card.fields ?? []).map((f, i) => (
            <Box key={i} sx={styles.cardField}>
              <Typography component="dt" sx={styles.cardLabel}>
                {f.label}
              </Typography>
              <Typography component="dd" sx={styles.cardValue}>
                <bdi>{f.value}</bdi>
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      {/* COMY1-392: a pricelist or product change, old → new per line, with what looks wrong flagged */}
      {card?.rows?.length > 0 && status === "proposed" && (
        <Box component="table" sx={styles.diff} aria-label={card.title}>
          <Box component="thead" sx={styles.visuallyHidden}>
            <tr>
              <th scope="col">{t("diff.item")}</th>
              <th scope="col">{t("diff.before")}</th>
              <td aria-hidden />
              <th scope="col">{t("diff.after")}</th>
            </tr>
          </Box>
          <tbody>
            {card.rows.map((r, i) => (
              <Box component="tr" key={i} sx={styles.diffRow}>
                <Box component="th" scope="row" sx={styles.diffLabel}>
                  <bdi>{r.label}</bdi>
                  {r.detail && (
                    <Typography component="span" sx={styles.diffDetail}>
                      <bdi>{r.detail}</bdi>
                    </Typography>
                  )}
                  {r.flag && (
                    <Typography component="span" sx={styles.diffFlag} role="note">
                      <WarnIcon sx={{ fontSize: "13px" }} /> {r.flag}
                    </Typography>
                  )}
                </Box>
                <Box component="td" sx={[styles.diffFrom, r.from == null && { textDecoration: "none" }]}>
                  <bdi>{r.from ?? t("diff.none")}</bdi>
                </Box>
                <Box component="td" sx={styles.diffArrow} aria-hidden>
                  {theme.direction === "rtl" ? "←" : "→"}
                </Box>
                <Box component="td" sx={styles.diffTo(r.to === null)}>
                  <bdi>{r.to ?? t("diff.removed")}</bdi>
                </Box>
              </Box>
            ))}
          </tbody>
        </Box>
      )}
      {card?.notes?.length > 0 && status === "proposed" && (
        <Box sx={styles.notes}>
          {card.notes.map((n, i) => (
            <Box key={i} sx={styles.note(n.key)} role={WARNINGS.includes(n.key) ? "alert" : undefined}>
              <Typography sx={styles.noteTitle}>{n.title}</Typography>
              <Typography sx={styles.noteText}>{n.text}</Typography>
              {n.items?.length > 0 && (
                <Box component="ul" sx={styles.noteItems}>
                  {n.items.map((item, j) => (
                    <li key={j}>
                      <bdi>{item}</bdi>
                    </li>
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
      {card?.attachments?.length > 0 && (
        <Box sx={styles.attachments}>
          {card.attachments.map((id) => (
            <AgentAttachment key={id} attachment={{ id, kind: "image", name: "" }} conversationId={conversationId} size={48} />
          ))}
        </Box>
      )}

      {status === "executed" && action.receipt && (
        <Box sx={styles.cardOutcome}>
          <DoneIcon sx={styles.outcomeIcon("done")} />
          <Typography sx={styles.receipt}>{action.receipt}</Typography>
        </Box>
      )}
      {status === "undone" && (
        <Box sx={styles.cardOutcome}>
          <UndoIcon sx={styles.outcomeIcon("muted")} />
          <Typography sx={styles.receipt}>{action.undoReceipt ?? t("actions.undone")}</Typography>
        </Box>
      )}
      {status === "failed" && (
        <Box sx={styles.cardOutcome} role="alert">
          <FailedIcon sx={styles.outcomeIcon("failed")} />
          <Typography sx={styles.receipt}>{action.error ?? action.message ?? t("actions.failed")}</Typography>
        </Box>
      )}
      {status === "expired" && <ExpiredIcon sx={styles.outcomeIcon("muted")} aria-hidden />}
      {note && status !== "failed" && <Typography sx={styles.cardNote}>{note}</Typography>}

      {status === "proposed" && (
        <Box sx={styles.cardButtons}>
          <Button
            variant="contained"
            size="small"
            disableElevation
            sx={styles.confirm}
            disabled={!!busy}
            onClick={() => onDecide(action, "confirm")}
          >
            {busy === "confirm" ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : t("actions.confirm")}
          </Button>
          <Button
            size="small"
            sx={styles.edit}
            disabled={!!busy}
            onClick={async () => {
              await onDecide(action, "decline", "edit");
              onEdit();
            }}
          >
            {t("actions.edit")}
          </Button>
          <Button variant="outlined" size="small" sx={styles.cancel} disabled={!!busy} onClick={() => onDecide(action, "decline", "cancel")}>
            {t("actions.cancel")}
          </Button>
        </Box>
      )}
      {canUndo && (
        <Box sx={styles.cardButtons}>
          <Button size="small" startIcon={<UndoIcon />} sx={styles.edit} disabled={!!busy} onClick={() => onDecide(action, "undo")}>
            {t("actions.undo")}
          </Button>
        </Box>
      )}
    </Box>
  );
}
